import React from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
}: ModalProps) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
        data-test-id="modal-backdrop"
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-auto"
          data-test-id="modal-content"
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b">
            <div>
              {title && (
                <h2
                  className="text-xl font-semibold text-gray-900"
                  data-test-id="modal-title"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  className="mt-1 text-sm text-gray-500"
                  data-test-id="modal-description"
                >
                  {description}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              data-test-id="modal-close-button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          {children}
        </div>
      </div>
    </>
  );
}

export function ModalFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
      {children}
    </div>
  );
}
